import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import DentalCertificateService from '../../../services/dentalCertificateService';
import PatientService from '../../../services/patientService';

import Button from '../../common/Button';
import MultiProcedureSelector from '../../common/MultiProcedureSelector';
import PatientSectionHeader from '../common/PatientSectionHeader';
import './NewCertificate.css'; // Reutilizamos los mismos estilos

const EditCertificate = () => {
  const navigate = useNavigate();
  const { patientId, certificateId } = useParams();
  const auth = useAuth();
  
  const [patient, setPatient] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    date: '',
    procedures: [],
    observations: ''
  });

  // Load patient, certificate and procedures data
  useEffect(() => {
    const loadData = async () => {
      if (!patientId || !certificateId || !auth.user?.access_token) {
        setError('ID de paciente o certificado no válido');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const token = auth.user.access_token;

        // Load patient and certificate in parallel
        const [patientData, certificateData] = await Promise.all([
          PatientService.getPatientById(patientId, token),
          DentalCertificateService.getDentalCertificateById(certificateId, token)
        ]);

        setPatient(patientData);
        setCertificate(certificateData);

        // Populate form with certificate data
        setFormData({
          date: certificateData.date ? certificateData.date.split('T')[0] : '', // Convert ISO date to YYYY-MM-DD
          procedures: certificateData.procedures || [],
          observations: certificateData.observations || ''
        });

      } catch (error) {
        console.error('Error loading data:', error);
        setError('Error al cargar los datos del certificado');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [patientId, certificateId, auth.user?.access_token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProceduresChange = (selectedProcedures) => {
    setFormData(prev => ({
      ...prev,
      procedures: selectedProcedures
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date) {
      setMessage({
        type: 'error',
        text: 'La fecha del certificado es requerida.'
      });
      return;
    }

    if (!formData.procedures || formData.procedures.length === 0) {
      setMessage({
        type: 'error',
        text: 'Debe seleccionar al menos un procedimiento.'
      });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      
      // Clean procedures data - only keep what's needed for certificates
      const cleanedProcedures = formData.procedures.map(proc => ({
        procedureId: proc.procedureId,
        procedureName: proc.procedureName,
        quantity: proc.quantity || 1
      }));
      
      const certificateData = {
        date: formData.date,
        procedures: cleanedProcedures,
        observations: formData.observations
      };
      
      await DentalCertificateService.updateDentalCertificate(certificateId, certificateData, token);
      
      // Navigate immediately after successful update
      navigate(`/patients/${patientId}/certificates`);

    } catch (error) {
      console.error('Error updating certificate:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Error al actualizar el certificado. Inténtalo de nuevo.'
      });
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/patients/${patientId}/certificates`);
  };

  const validateForm = () => {
    if (!formData.date) {
      setMessage({
        type: 'error',
        text: 'La fecha es requerida'
      });
      return false;
    }

    if (!formData.procedures || formData.procedures.length === 0) {
      setMessage({
        type: 'error',
        text: 'Debe seleccionar al menos un procedimiento'
      });
      return false;
    }

    return true;
  };

  const handlePreview = () => {
    if (!validateForm()) {
      return;
    }

    const certificateData = {
      id: 'PREVIEW',
      date: formData.date,
      procedures: formData.procedures,
      observations: formData.observations
    };

    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateCertificateHTML(certificateData));
    printWindow.document.close();
  };

  const generateCertificateHTML = (certificate) => {
    const patientName = patient ? `${patient.firstName} ${patient.lastName}` : 'Paciente';
    const certificateDate = certificate.date ? new Date(certificate.date).toLocaleDateString('es-ES') : new Date().toLocaleDateString('es-ES');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Certificado Dental - ${patientName}</title>
        <style>
          body {
            font-family: 'Times New Roman', serif;
            margin: 20px;
            line-height: 1.4;
            color: #333;
            background: #fff;
          }
          .certificate-container {
            max-width: 700px;
            margin: 0 auto;
            border: 2px solid #2d3748;
            padding: 25px;
            position: relative;
            min-height: calc(11in - 40px);
            box-sizing: border-box;
          }
          .certificate-header {
            text-align: center;
            border-bottom: 1px solid #2d3748;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .clinic-logo {
            width: 60px;
            height: 60px;
            background: #f7fafc;
            border: 2px solid #2d3748;
            border-radius: 50%;
            margin: 0 auto 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            font-weight: bold;
            color: #2d3748;
          }
          .clinic-info h1 {
            font-size: 22px;
            font-weight: bold;
            color: #2d3748;
            margin: 8px 0;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .clinic-info h2 {
            font-size: 16px;
            color: #4a5568;
            margin: 3px 0;
            font-weight: normal;
          }
          .doctor-credentials {
            font-size: 12px;
            color: #718096;
            margin: 8px 0;
          }
          .certificate-title {
            font-size: 24px;
            font-weight: bold;
            color: #2d3748;
            margin: 15px 0;
            text-transform: uppercase;
            letter-spacing: 2px;
            text-decoration: underline;
          }
          .certificate-content {
            font-size: 14px;
            line-height: 1.5;
            text-align: justify;
            margin: 20px 0;
            padding: 10px 0;
          }
          .patient-name {
            font-weight: bold;
            text-transform: uppercase;
            text-decoration: underline;
          }

          .signature-section {
            margin-top: 40px;
            display: flex;
            justify-content: space-between;
            align-items: end;
          }
          .date-section {
            text-align: left;
            font-size: 14px;
          }
          .signature-box {
            text-align: center;
            width: 200px;
          }
          .signature-line {
            border-top: 2px solid #2d3748;
            margin: 40px 0 10px 0;
          }
          .doctor-name {
            font-weight: bold;
            font-size: 14px;
            color: #2d3748;
          }
          .doctor-title {
            font-size: 12px;
            color: #4a5568;
            margin-top: 3px;
          }
          .certificate-footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #e2e8f0;
            text-align: center;
            color: #718096;
            font-size: 10px;
          }
          .contact-info {
            margin: 10px 0;
          }
          .certificate-number {
            position: absolute;
            top: 15px;
            right: 15px;
            font-size: 12px;
            color: #718096;
          }
          .preview-watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 48px;
            color: rgba(255, 0, 0, 0.1);
            font-weight: bold;
            z-index: 10;
            pointer-events: none;
          }
          @media print {
            body { 
              margin: 0; 
              padding: 0;
            }
            .certificate-container { 
              margin: 15px;
              padding: 20px;
              break-inside: avoid;
              box-shadow: none;
              page-break-inside: avoid;
              max-height: calc(11in - 30px);
              overflow: hidden;
            }
            .preview-watermark { display: none; }
            .signature-section {
              margin-top: 20px;
            }
            .certificate-footer {
              margin-top: 15px;
              font-size: 9px;
            }
          }
        </style>
      </head>
      <body>
        <div class="certificate-container">
          ${certificate.id === 'PREVIEW' ? '<div class="preview-watermark">VISTA PREVIA</div>' : ''}
          
          <div class="certificate-number">
            Certificado No. ${certificate.id || 'XXXX'}
          </div>
          
          <div class="certificate-header">
            <div class="clinic-logo">
              🦷
            </div>
            <div class="clinic-info">
              <h1>Clínica Dental</h1>
              <h2>Dr. ${auth.user?.name || 'Nombre del Doctor'}</h2>
              <div class="doctor-credentials">
                Cirujano Dentista<br>
                Cédula Profesional: XXXXXXXXX
              </div>
            </div>
          </div>

          <h1 class="certificate-title">Certificado Dental</h1>

          <div class="certificate-content">
            <p>
              Por medio del presente documento, <strong>CERTIFICO</strong> que el/la paciente 
              <span class="patient-name">${patientName}</span>, 
              ${patient?.idNumber ? `con documento de identidad No. ${patient.idNumber},` : ''}
              ha sido examinado(a) y tratado(a) en esta clínica dental.
            </p>

            ${certificate.procedures && certificate.procedures.length > 0 ? `
              <p>
                <strong>Procedimiento${certificate.procedures.length > 1 ? 's' : ''} realizado${certificate.procedures.length > 1 ? 's' : ''}:</strong> 
                ${certificate.procedures.map(proc => `${proc.procedureName}${proc.quantity > 1 ? ` (${proc.quantity} ${proc.quantity > 1 ? 'sesiones' : 'sesión'})` : ''}`).join(', ')}.
              </p>
            ` : ''}

            ${certificate.observations ? `
              <p>
                <strong>Observaciones médicas:</strong> ${certificate.observations}
              </p>
            ` : ''}

            <p>
              Se extiende el presente certificado a solicitud del interesado para los fines que estime convenientes.
            </p>
          </div>

          <div class="signature-section">
            <div class="date-section">
              <p><strong>Fecha:</strong> ${certificateDate}</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="doctor-name">Dr. ${auth.user?.name || 'Nombre del Doctor'}</div>
              <div class="doctor-title">Cirujano Dentista</div>
              <div class="doctor-title">Cédula: XXXXXXXXX</div>
            </div>
          </div>

          <div class="certificate-footer">
            <div class="contact-info">
              <strong>Clínica Dental</strong><br>
              Dirección: Calle Principal #123, Ciudad<br>
              Teléfono: (XX) XXXX-XXXX | Email: contacto@clinica.com<br>
              www.clinicadental.com
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  if (isLoading) {
    return (
      <div className="new-certificate-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Cargando certificado...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="new-certificate-container">
        <PatientSectionHeader
          patientId={patientId}
          patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
          sectionTitle="Editar Certificado Dental"
          onBackClick={handleCancel}
        />
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      </div>
    );
  }

  if (!patient || !certificate) {
    return (
      <div className="new-certificate-container">
        <PatientSectionHeader
          patientId={patientId}
          patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
          sectionTitle="Editar Certificado Dental"
          onBackClick={handleCancel}
        />
        <div className="error-message">
          <i className="fas fa-file-medical"></i>
          No se pudo cargar la información del certificado.
        </div>
      </div>
    );
  }

  return (
    <div className="new-certificate-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Editar Certificado Dental"
        onBackClick={handleCancel}
      />

      <div className="certificate-form-content">
        {message.text && (
          <div className={`message ${message.type}`}>
            <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="certificate-form">
          <div className="form-section">
            <h3>Información del Certificado</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">
                  <i className="fas fa-calendar"></i>
                  Fecha del Certificado *
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>
                  <i className="fas fa-tooth"></i>
                  Procedimientos Realizados *
                </label>
                <MultiProcedureSelector
                  selectedProcedures={formData.procedures}
                  onProceduresChange={handleProceduresChange}
                  placeholder="Buscar y agregar procedimientos..."
                  showCost={false}
                />
                <small className="field-hint">
                  {formData.procedures.length} procedimiento{formData.procedures.length !== 1 ? 's' : ''} seleccionado{formData.procedures.length !== 1 ? 's' : ''}
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="observations">
                  <i className="fas fa-sticky-note"></i>
                  Observaciones Médicas
                </label>
                <textarea
                  id="observations"
                  name="observations"
                  value={formData.observations}
                  onChange={handleInputChange}
                  placeholder="Escriba aquí cualquier observación adicional sobre el tratamiento o estado del paciente..."
                  rows="4"
                />
                <small className="field-hint">
                  Opcional. Incluya cualquier información relevante que deba aparecer en el certificado.
                </small>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            
            <Button
              type="button"
              variant="secondary"
              icon="fas fa-eye"
              onClick={handlePreview}
              disabled={isSubmitting || !formData.procedures || formData.procedures.length === 0}
            >
              Vista Previa
            </Button>

            <Button
              type="submit"
              variant="primary"
              icon={isSubmitting ? "fas fa-spinner fa-spin" : "fas fa-save"}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Actualizando...' : 'Actualizar Certificado'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCertificate;
